/**
 * tableControls — small, framework-free DOM-string helpers so every page
 * filters & paginates the same way and looks consistent (indigo accent, tokens).
 *
 * These are PURE helpers: they return HTML strings (or compute a page slice) and
 * hold no state. The owning page keeps its own filter/page state and re-renders.
 *
 * ── How a page wires it (the EventPricingPage pattern) ──────────────────────
 *
 *   // 1. State lives on the page:
 *   private search = '';
 *   private statusFilter = 'all';
 *   private page = 1;
 *   private readonly pageSize = 25;
 *
 *   // 2. In render(): derive the filtered list, paginate, draw the bar + pager.
 *   private renderTable(): void {
 *     const filtered = this.allRows.filter(r =>
 *       (!this.search || r.name.toLowerCase().includes(this.search.toLowerCase())) &&
 *       (this.statusFilter === 'all' || r.status === this.statusFilter)
 *     );
 *     const pg = paginate(filtered, this.page, this.pageSize);
 *     this.page = pg.page; // adopt the clamped page
 *
 *     el.innerHTML = `
 *       ${filterBar({
 *         searchId: 'tbl-search',
 *         searchValue: this.search,
 *         searchPlaceholder: 'Search units…',
 *         selects: [{ id: 'tbl-status', value: this.statusFilter, options: [
 *           { value: 'all', label: 'All statuses' },
 *           { value: 'active', label: 'Active' },
 *         ]}],
 *         countLabel: `${pg.total} unit${pg.total === 1 ? '' : 's'}`,
 *       })}
 *       <table>…${pg.items.map(rowHtml).join('')}…</table>
 *       ${pager({ idPrefix: 'tbl', page: pg.page, pages: pg.pages, total: pg.total, pageSize: this.pageSize })}
 *     `;
 *     this.bindTableControls();
 *   }
 *
 *   // 3. Read control values → set state → re-render → refocus the search box.
 *   private bindTableControls(): void {
 *     const search = document.getElementById('tbl-search') as HTMLInputElement | null;
 *     search?.addEventListener('input', () => {
 *       this.search = search.value; this.page = 1; this.renderTable();
 *       const next = document.getElementById('tbl-search') as HTMLInputElement | null;
 *       if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); }
 *     });
 *     const status = document.getElementById('tbl-status') as HTMLSelectElement | null;
 *     status?.addEventListener('change', () => { this.statusFilter = status.value; this.page = 1; this.renderTable(); });
 *     document.getElementById('tbl-prev')?.addEventListener('click', () => { this.page--; this.renderTable(); });
 *     document.getElementById('tbl-next')?.addEventListener('click', () => { this.page++; this.renderTable(); });
 *   }
 */

import { Formatters } from './formatters';

// ── paginate ─────────────────────────────────────────────────────────────────

export interface PaginateResult<T> {
  items: T[];   // the slice for the (clamped) current page
  page: number; // clamped page, 1-based
  pages: number; // total page count (>= 1)
  total: number; // total item count
  start: number; // 1-based index of first item shown (0 when empty)
  end: number;   // 1-based index of last item shown (0 when empty)
}

/**
 * Slice `items` to one page. Clamps `page` into [1, pages] so an out-of-range
 * page (e.g. after a filter shrinks the list) snaps back into range.
 */
export function paginate<T>(items: T[], page: number, pageSize: number): PaginateResult<T> {
  const list = Array.isArray(items) ? items : [];
  const total = list.length;
  const size = Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : 25;
  const pages = Math.max(1, Math.ceil(total / size));

  let p = Number.isFinite(page) ? Math.floor(page) : 1;
  if (p < 1) p = 1;
  if (p > pages) p = pages;

  const startIdx = (p - 1) * size;
  const slice = list.slice(startIdx, startIdx + size);

  return {
    items: slice,
    page: p,
    pages,
    total,
    start: total === 0 ? 0 : startIdx + 1,
    end: total === 0 ? 0 : startIdx + slice.length,
  };
}

// ── shared styles (injected once per bar; cheap + idempotent via id) ─────────
// Scoped to .tc-* classes; matches EventPricingPage's indigo/token look.

const TC_STYLE_ID = 'tc-controls-style';

function tcStyles(): string {
  return `
    <style id="${TC_STYLE_ID}">
      .tc-bar { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin:0 0 14px; }
      .tc-bar .tc-search { position:relative; flex:1 1 220px; min-width:0; }
      .tc-bar .tc-search input { width:100%; box-sizing:border-box; padding:9px 11px 9px 32px; border:1px solid var(--border); border-radius:8px; font-size:13px; font-family:inherit; background:#fff; color:var(--text); transition:border-color .12s, box-shadow .12s; }
      .tc-bar .tc-search input:focus { outline:none; border-color:#3b82f6; box-shadow:0 0 0 3px rgba(37,99,235,.12); }
      .tc-bar .tc-search .tc-search-ico { position:absolute; left:11px; top:50%; transform:translateY(-50%); font-size:13px; color:var(--text-muted); pointer-events:none; line-height:1; }
      .tc-bar select.tc-select { flex:0 0 auto; box-sizing:border-box; padding:9px 11px; border:1px solid var(--border); border-radius:8px; font-size:13px; font-family:inherit; background:#fff; color:var(--text); cursor:pointer; transition:border-color .12s, box-shadow .12s; }
      .tc-bar select.tc-select:focus { outline:none; border-color:#3b82f6; box-shadow:0 0 0 3px rgba(37,99,235,.12); }
      .tc-bar .tc-count { margin-left:auto; font-size:12px; color:var(--text-muted); white-space:nowrap; }

      .tc-pager { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; margin:14px 0 0; }
      .tc-pager .tc-pager-info { font-size:12px; color:var(--text-muted); }
      .tc-pager .tc-pager-btns { display:flex; align-items:center; gap:8px; }
      .tc-pager button { padding:7px 14px; border-radius:8px; border:1px solid var(--border); background:#fff; color:var(--text); font-size:12px; font-weight:600; cursor:pointer; transition:border-color .12s, color .12s, background .12s; }
      .tc-pager button:hover:not(:disabled) { border-color:#3b82f6; color:#3b82f6; background:#f5f3ff; }
      .tc-pager button:disabled { color:#cbd5e1; cursor:default; background:#fff; border-color:var(--border); }

      @media (max-width: 640px) {
        .tc-bar { gap:8px; }
        .tc-bar .tc-count { margin-left:0; flex-basis:100%; }
        .tc-bar select.tc-select { flex:1 1 auto; }
        .tc-pager { gap:8px; }
      }
    </style>`;
}

// ── filterBar ────────────────────────────────────────────────────────────────

export interface FilterBarSelect {
  id: string;
  value: string;
  options: Array<{ value: string; label: string }>;
}

export interface FilterBarOptions {
  searchId?: string;
  searchValue?: string;
  searchPlaceholder?: string;
  selects?: FilterBarSelect[];
  countLabel?: string;
}

/**
 * A styled toolbar: optional search input + 0..n <select>s + a right-aligned
 * count note. Responsive (wraps on narrow). Returns '' for nothing requested.
 */
export function filterBar(opts: FilterBarOptions): string {
  const search = opts.searchId
    ? `<div class="tc-search">
         <span class="tc-search-ico">🔍</span>
         <input id="${Formatters.escapeHtml(opts.searchId)}" type="search"
                placeholder="${Formatters.escapeHtml(opts.searchPlaceholder || 'Search…')}"
                value="${Formatters.escapeHtml(opts.searchValue || '')}" />
       </div>`
    : '';

  const selects = (opts.selects || []).map(sel => {
    const options = (sel.options || []).map(o => {
      const selected = o.value === sel.value ? ' selected' : '';
      return `<option value="${Formatters.escapeHtml(o.value)}"${selected}>${Formatters.escapeHtml(o.label)}</option>`;
    }).join('');
    return `<select id="${Formatters.escapeHtml(sel.id)}" class="tc-select">${options}</select>`;
  }).join('');

  const count = opts.countLabel
    ? `<span class="tc-count">${Formatters.escapeHtml(opts.countLabel)}</span>`
    : '';

  if (!search && !selects && !count) return '';

  return `${tcStyles()}<div class="tc-bar">${search}${selects}${count}</div>`;
}

// ── pager ────────────────────────────────────────────────────────────────────

export interface PagerOptions {
  idPrefix: string;
  page: number;
  pages: number;
  total: number;
  pageSize: number;
}

/**
 * Prev/Next buttons (`${idPrefix}-prev` / `${idPrefix}-next`, disabled at ends)
 * + "Showing X–Y of N". Renders nothing when there is one page or fewer.
 */
export function pager(opts: PagerOptions): string {
  const pages = Number.isFinite(opts.pages) ? opts.pages : 1;
  if (pages <= 1) return '';

  const page = Number.isFinite(opts.page) ? opts.page : 1;
  const total = Number.isFinite(opts.total) ? opts.total : 0;
  const size = Number.isFinite(opts.pageSize) && opts.pageSize > 0 ? opts.pageSize : 1;

  const start = total === 0 ? 0 : (page - 1) * size + 1;
  const end = Math.min(page * size, total);
  const prefix = Formatters.escapeHtml(opts.idPrefix);

  const startTxt = Number.isFinite(start) ? start : '—';
  const endTxt = Number.isFinite(end) ? end : '—';
  const totalTxt = Number.isFinite(total) ? total : '—';

  return `${tcStyles()}<div class="tc-pager">
      <span class="tc-pager-info">Showing ${startTxt}–${endTxt} of ${totalTxt}</span>
      <div class="tc-pager-btns">
        <button id="${prefix}-prev" type="button"${page <= 1 ? ' disabled' : ''}>‹ Prev</button>
        <button id="${prefix}-next" type="button"${page >= pages ? ' disabled' : ''}>Next ›</button>
      </div>
    </div>`;
}
